// „Номерация на документи“ в модала за фирма: последният издаден номер и
// прегледът кой ще е следващият.
//
// Прегледът повтаря ТОЧНО сметката на модала за фактуриране
// (`InvoiceCreateModal`: последният номер + 1, допълнен до 10 цифри). Разминат
// ли се двете места, операторът вижда едно число тук и друго върху документа —
// затова формулата е една, в `nextInvoiceNumberOf`, и се внася и от двете.

import { Input, InputNumber } from "antd";
import { useTranslation } from "react-i18next";

import {
  INVOICE_NUMBER_LENGTH,
  nextInvoiceNumberOf,
} from "../../../../lib/invoiceNumbering";
import { Field, Row, Section } from "./companyFormFields";

type Props = {
  /** `null` = полето е празно; още не се знае откъде тръгва номерацията. */
  lastInvoiceNumber: number | null;
  onLastInvoiceNumberChange: (value: number | null) => void;
  lastProformaNumber: string;
  onLastProformaNumberChange: (value: string) => void;
  invoiceError?: string;
  proformaError?: string;
  isNarrow: boolean;
};

export const NumberingSection = ({
  lastInvoiceNumber,
  onLastInvoiceNumberChange,
  lastProformaNumber,
  onLastProformaNumberChange,
  invoiceError,
  proformaError,
  isNarrow,
}: Props) => {
  const { t } = useTranslation();

  // Празно поле няма следващ номер — показва се подканата, не измислено число.
  const nextNumber =
    lastInvoiceNumber === null ? null : nextInvoiceNumberOf(lastInvoiceNumber);

  return (
    <Section title={t("companiesConfig.secNumbering")}>
      {/* Полетата за номерация се показват винаги — превключвателят
          „Отделна номерация“ е премахнат по продуктово решение. */}
      <Row narrow={isNarrow}>
        <Field
          label={t("companiesConfig.fieldLastInvoice")}
          hint={t("companiesConfig.fieldLastInvoiceHint")}
          error={invoiceError}
          required
        >
          <InputNumber
            className={invoiceError ? "co-err" : ""}
            placeholder={t("companiesConfig.fieldLastInvoicePlaceholder")}
            value={lastInvoiceNumber}
            min={0}
            max={9_999_999_999}
            step={1}
            precision={0}
            onChange={(value) =>
              onLastInvoiceNumberChange(
                value === null || value === undefined
                  ? null
                  : Math.floor(Number(value)),
              )
            }
            formatter={(value) => {
              // antd подава "" (не null) при празна стойност, макар типът да
              // казва number — без проверката padStart я прави „0000000000“.
              const digits = String(value ?? "");
              return digits === ""
                ? ""
                : digits.padStart(INVOICE_NUMBER_LENGTH, "0");
            }}
            parser={(value) => {
              // Празен вход остава празен (→ onChange(null)); иначе Number("")
              // = 0 попълва полето обратно при изтриване. antd приема "" от
              // parser-а, но типът му е само number.
              const digits = (value ?? "").replace(/\D/g, "");
              return (digits === "" ? "" : Number(digits)) as unknown as number;
            }}
          />
        </Field>

        <Field
          label={t("companiesConfig.fieldLastProforma")}
          hint={t("companiesConfig.fieldLastProformaHint")}
          error={proformaError}
        >
          <Input
            className={proformaError ? "co-err" : ""}
            value={lastProformaNumber}
            maxLength={20}
            onChange={(event) =>
              onLastProformaNumberChange(event.target.value.toUpperCase())
            }
            placeholder="П-2026-0001"
          />
        </Field>
      </Row>

      <NextDocumentPreview nextNumber={nextNumber} />
    </Section>
  );
};

/** Лентата „следващият документ ще е № …“ под полетата. */
const NextDocumentPreview = ({ nextNumber }: { nextNumber: string | null }) => {
  const { t } = useTranslation();

  return (
    <div
      className={`flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-lg border px-3 py-2.5 ${
        nextNumber
          ? "border-brand-tint2 bg-brand-tint"
          : "border-shade-2 bg-fill"
      }`}
    >
      <span
        className={`text-[10px] font-bold uppercase leading-[1.2] tracking-[0.11em] ${
          nextNumber ? "text-brand-deep-ink" : "text-muted"
        }`}
      >
        {t("companiesConfig.numberingNextKicker")}
      </span>

      {nextNumber ? (
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-[12px] font-semibold leading-[1.3] text-brand-deep-ink">
            {t("companiesConfig.numberingNextInvoice")}
          </span>
          <span className="font-pos-mono text-[16px] font-extrabold leading-[1.2] tracking-[0.04em] tabular-nums text-brand-deep-ink">
            {nextNumber}
          </span>
        </span>
      ) : (
        <span className="text-[12px] font-medium leading-[1.35] text-soft">
          {t("companiesConfig.numberingNextUnknown")}
        </span>
      )}
    </div>
  );
};
